import { intersection, isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { getTaskIdListBySectionId } from '../../sections/selectors';
import {
  getOpenedTaskId,
  getTasksIdListByProjectId,
  isSortingApplied,
} from '../../general/selectors';
import { createDeepEqualSelector } from '../../utils/customFunctions';
import { getAssigneesStore } from '../../assignees/selectors';
import { getUserIdSelector } from '../../user/selectors';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

const getTasksStore = (state) => state?.tasks?.entities || DEFAULT_OBJECT;
const getTaskIdProp = (state, props) => props?.taskId;
const getWorkspaceIdProp = (state, props) => props?.workspaceId;

export const getTaskById = createSelector(
  [getTasksStore, getTaskIdProp],
  (tasks, taskId) => {
    const result = tasks?.[taskId];
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const makeGetTaskById = () => getTaskById;

export const getAllTasks = createSelector([getTasksStore], (tasks) =>
  Object.values(tasks),
);

export const getTasksByWorkspaceId = createSelector(
  [getAllTasks, getWorkspaceIdProp],
  (tasks, workspaceId) =>
    tasks.filter((task) => task.workspace === workspaceId),
);

export const getSortedTaskIdListBySectionId = createSelector(
  [
    getTaskIdListBySectionId,
    getTasksIdListByProjectId,
    getTasksStore,
    isSortingApplied,
  ],
  (taskIdListBySectionId, filteredTasksIdList, tasks, hasSorting) => {
    let tasksList = taskIdListBySectionId?.map(
      (taskId) => tasks?.[taskId] || {},
    );
    if (!hasSorting) {
      tasksList = tasksList?.sort((a, b) => a.position - b.position);
    }

    const result = tasksList?.map((task) => task?._id) || DEFAULT_ARRAY;
    const filteredResult = intersection(result, filteredTasksIdList);
    return isEmpty(filteredResult) ? DEFAULT_ARRAY : filteredResult;
  },
);

export const makeGetSortedTaskIdListBySectionIdSelector = () =>
  createDeepEqualSelector(getSortedTaskIdListBySectionId, (tasksIdList) =>
    tasksIdList.map((id) => id),
  );

export const isTaskCompletedSelector = createSelector(
  getTaskById,
  (task) => task?.isCompleted,
);

export const makeIsTaskCompletedSelector = () => isTaskCompletedSelector;

const getTaskRecurringConfig = createSelector(getTaskById, (task) => {
  const result = task?.recurringConfig;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const isRecurringTask = createSelector(
  getTaskRecurringConfig,
  (recurringConfig) => recurringConfig?.recurring,
);

export const makeIsRecurringTask = () => isRecurringTask;

export const getRecurringDuration = createSelector(
  getTaskRecurringConfig,
  (recurringConfig) => recurringConfig?.recurringDuration,
);

export const makeGetRecurringDuration = () => getRecurringDuration;

export const getTaskDueDate = createSelector(
  getTaskById,
  (task) => task?.dueDate,
);

export const makeGetTaskDueDate = () => getTaskDueDate;

export const getTaskIsCompleted = createSelector(
  getTaskById,
  (task) => task?.isCompleted,
);

export const makeGetTaskIsCompleted = () => getTaskIsCompleted;

export const getTaskProjectWithSectionIdList = createSelector(
  getTaskById,
  (task) => {
    const result = task?.projects?.map((prj) => ({
      project: prj?.projectId?._id || '',
      section: prj?.sectionId?._id || '',
    }));
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getTaskProjectIdListSelector = createSelector(
  getTaskById,
  (task) => {
    const result = task?.projectAndSectionLabelList
      ? Object.keys(task?.projectAndSectionLabelList)
      : task?.projects?.map(
          (prj) => prj?.projectId?._id || prj?.projectId || '',
        );
    return isEmpty(result)
      ? task?.projectId
        ? [task?.projectId]
        : DEFAULT_ARRAY
      : result;
  },
);

export const makeGetTaskProjectWithSectionIdList = () =>
  getTaskProjectWithSectionIdList;

export const getTaskParentId = createSelector(
  getTaskById,
  (task) => task?.parentTaskId,
);

export const makeGetTaskParentId = () => getTaskParentId;

export const getTaskTitle = createSelector(
  getTaskById,
  (task) => task?.taskTitle,
);

export const makeGetTaskTitle = () => getTaskTitle;

export const getTaskAssignees = createSelector(
  [getTaskById, getAssigneesStore],
  (task, assignees) => {
    const result = task?.assignee?.map((assigneeId) => ({
      assigneeId: assigneeId?.assigneeId
        ? assignees[assigneeId?.assigneeId?._id]
        : assignees[assigneeId],
    }));

    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const makeGetTaskAssignees = () => getTaskAssignees;

export const getTaskPriority = createSelector(
  getTaskById,
  (task) => task?.priority,
);

export const makeGetTaskPriority = () => getTaskPriority;

export const getTaskTags = createSelector(getTaskById, (task) => task?.tags);

export const makeGetTaskTags = () => getTaskTags;

export const getTaskPosition = createSelector(
  getTaskById,
  (task) => task?.position,
);

export const makeGetTaskPosition = () => getTaskPosition;

export const getSubTaskOrder = createSelector(getTaskById, (task) => {
  const result = task?.subTaskOrder;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const makeGetSubTaskOrder = () => getSubTaskOrder;

export const getTaskTitleIdentifier = createSelector(
  getTaskById,
  (task) => task?.titleIdentifier,
);

export const makeGetTaskTitleIdentifier = () => getTaskTitleIdentifier;

export const getTaskSource = createSelector(getTaskById, (task) => task?.source);
export const makeGetTaskSource = () => getTaskSource;

export const getTaskFiles = createSelector(getTaskById, (task) => {
  const result = task?.files;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const makeGetTaskFiles = () => getTaskFiles;

export const getTaskPreviewFileId = createSelector(
  getTaskById,
  (task) => task?.previewFile,
);

export const getTaskPreviewFile = createSelector(
  [getTaskFiles, getTaskPreviewFileId],
  (files, previewFileId) => {
    const result = files.find((file) => file?._id === previewFileId);
    return isEmpty(result) ? null : result;
  },
);

export const makeGetTaskPreviewFile = () => getTaskPreviewFile;

export const getTaskCustomFields = createSelector(getTaskById, (task) => {
  const result = task?.customFields || task?.extendedCustomFields;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const makeGetTaskCustomFields = () => getTaskCustomFields;

export const getOpenedTask = createSelector(
  [getOpenedTaskId, getTasksStore],
  (taskId, tasks) => {
    const result = tasks?.[taskId];
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getTaskSectionId = createSelector(
  getTaskById,
  (task) => task?.sectionId,
);

export const getTaskCreated = createSelector(
  getTaskById,
  (task) => task?.createdAt,
);

export const makeGetTaskCreated = () => getTaskCreated;

export const getTaskUpdated = createSelector(
  getTaskById,
  (task) => task?.updatedAt,
);

export const makeGetTaskUpdated = () => getTaskUpdated;

const getTaskCustomFieldsIdList = createSelector(
  getTaskCustomFields,
  (customFields) => customFields.map((customField) => customField?._id),
);

export const makeGetTaskCustomFieldsIdList = () => getTaskCustomFieldsIdList;

const getCustomFieldIdProp = (state, props) => props?.customFieldId;

const getTaskCustomFieldById = createSelector(
  [getTaskCustomFields, getCustomFieldIdProp],
  (customFields, fieldId) => {
    const result = customFields.find((field) => field?._id === fieldId);
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getTaskCustomFieldValue = createSelector(
  getTaskCustomFieldById,
  (customField) => customField?.value,
);

export const makeGetTaskCustomFieldValue = () => getTaskCustomFieldValue;

export const getSubtaskIdList = createSelector(getTaskById, (task) => {
  const result = task?.subtasks;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

const getTaskIdsToVerify = (state, props) => props?.taskIdList || DEFAULT_ARRAY;

export const hasMissingTaskInStore = createSelector(
  [getTasksStore, getTaskIdsToVerify],
  (tasks, tasksToVerify) => {
    const allTasksIdList = Object.keys(tasks) || DEFAULT_ARRAY;
    return tasksToVerify.some(
      (taskIdToVerify) => !allTasksIdList.includes(taskIdToVerify),
    );
  },
);

export const makeHasMissingTaskInStore = () => hasMissingTaskInStore;

export const getTaskCreatedBy = createSelector(
  getTaskById,
  (task) => task?.createdBy,
);

export const isTaskCreator = createSelector(
  [getTaskCreatedBy, getUserIdSelector],
  (taskCreatorId, userId) => taskCreatorId === userId,
);

export const getOpenedTaskTitle = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.taskTitle,
);

export const getOpenedTaskParentTaskId = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.parentTaskId,
);

const getOpenedTaskParentTask = createSelector(getOpenedTask, (openedTask) => {
  const result = openedTask?.parentTask;
  return isEmpty(result) ? DEFAULT_OBJECT : result;
});

export const getOpenedTaskParentTaskTitle = createSelector(
  getOpenedTaskParentTask,
  (openedTaskParentTask) => openedTaskParentTask?.taskTitle,
);

export const getOpenedTaskParentTaskTitleIdentifier = createSelector(
  getOpenedTaskParentTask,
  (openedTaskParentTask) => openedTaskParentTask?.titleIdentifier,
);

export const isOpenedTaskCompleted = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.isCompleted,
);

export const getOpenedTaskFiles = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.files;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getOpenedTaskCreatedAt = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.createdAt,
);

export const getOpenedTaskCreatedBy = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.createdBy,
);

export const getOpenedTaskPriority = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.priority,
);

export const getOpenedTaskDueDate = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.dueDate,
);

export const getOpenedTaskProjects = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.projects;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getOpenedTaskRecurringConfig = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.recurringConfig;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const isOpenedTaskRecurring = createSelector(
  getOpenedTaskRecurringConfig,
  (recurringConfig) => recurringConfig?.recurring,
);

export const getOpenedTaskTitleIdentifier = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.titleIdentifier,
);

export const getOpenedTaskExtendedCustomFields = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.extendedCustomFields;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getOpenedTaskDescription = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.taskDescription,
);

export const getOpenedTaskTags = createSelector(getOpenedTask, (openedTask) => {
  const result = openedTask?.tags;
  return isEmpty(result) ? DEFAULT_ARRAY : result;
});

export const getOpenedTaskWorkspace = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.workspace,
);

export const getOpenedTaskParticipants = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.participants;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getOpenedTaskRoom = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.room,
);

export const getOpnedTask = createSelector(
  getOpenedTask,
  (openedTask) => openedTask,
);

export const getOpenedTaskComments = createSelector(
  getOpenedTask,
  (openedTask) => {
    const result = openedTask?.comments;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const isOpenedTaskLoading = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.isLoading,
);

export const getOpenedTaskPaymentTransaction = createSelector(
  getOpenedTask,
  (openedTask) => openedTask?.paymentTransaction || null,
);
